/**
 * versona_chatter.ts — PURE ambient conversation for CHATTING pairs
 * (eavesdrop bubbles, 2026-09-10).
 *
 * The server stages a true exchange for every encounter it runs (a vented
 * grudge, a rumor, small talk — `npc_encounter.smallTalk`), but the far more
 * common sight is two versonas simply standing together in the `chatting`
 * state (hasCompany in tickVersonaAI) with no content behind it. This layer
 * gives THOSE pairs something to be overheard: a deterministic thread of
 * turns per pair — topics drawn from the world's own lore bank (the same
 * entries the Morrowind-style topic window answers from) and small talk
 * about the weather and the hour — shown only to a player close enough to
 * listen. Client-only, never authority: a thread is a pure function of
 * (pair, turn), so two players standing by the same pair overhear the same
 * words.
 */
import { LORE_PLACES, LORE_SPACE, LORE_TECH, LORE_NATURE, type LoreEntry } from './topic_lore.js';
import { LORE_HISTORY, LORE_SOCIETY, LORE_TRADES, LORE_PERSONAL, LORE_B } from './topic_lore2.js';
import { LORE_GOODS, LORE_CREATURES, LORE_SETTLEMENTS, LORE_FIELDCRAFT, LORE_C } from './topic_lore3.js';

/** You must be this close to overhear anything at all (m). */
export const CHATTER_RANGE_M = 16;
/** Inside this distance the words are fully legible; they fade out to the range (m). */
export const CHATTER_FULL_M = 11;
/** Seconds each turn stays up before the other speaks. */
export const CHATTER_TURN_S = 5.5;
/** At most this many conversations are overheard at once (the nearest). */
export const CHATTER_MAX_THREADS = 3;
/** Two chatting versonas this close are one conversation (m). */
export const CHATTER_PAIR_M = 4.5;
/** States that count as "in conversation" for the ambient layer. */
export const CHATTER_STATES: ReadonlySet<string> = new Set(['chatting', 'social']);

/** The lore bank the chatter draws from — surface answers only, no war/civ-gated entries. */
const LORE: LoreEntry[] = [
  ...LORE_PLACES, ...LORE_SPACE, ...LORE_TECH, ...LORE_NATURE,
  ...LORE_HISTORY, ...LORE_SOCIETY, ...LORE_TRADES, ...LORE_PERSONAL, ...LORE_B,
  ...LORE_GOODS, ...LORE_CREATURES, ...LORE_SETTLEMENTS, ...LORE_FIELDCRAFT, ...LORE_C,
].filter((e) => !e.war && !e.civ);

function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
/** fnv + the murmur3 finalizer: consecutive keys land far apart (the HASH LAW). */
function mix01(s: string): number {
  let h = fnv(s);
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
function pick<T>(arr: readonly T[], u: number): T { return arr[Math.min(arr.length - 1, Math.floor(u * arr.length))]; }

/** One key per unordered pair. */
export function pairKey(a: string, b: string): string { return a < b ? `${a}|${b}` : `${b}|${a}`; }

export interface ChatterFacts {
  /** the weather emitter kind at the player: none | rain | snow */
  precip: string;
  /** time of day 0..1 */
  tod: number;
}

const OPENERS = ['{L}? {S}', 'Speaking of {l} — {S}', 'You know what they say about {l}. {S}', 'I was down by {l} today. {S}', '{S} That is {l} for you.'];
const REACTIONS = ['Is that so.', 'Aye, so I hear.', 'Hm. I had it differently.', 'Say that again?', 'That is what my mother said, word for word.', 'You would know.', 'Keep your voice down about it.', 'And yet here we are.'];
const SMALL_TALK: Record<string, string[][]> = {
  none: [['Fine sky today.', 'Make the most of it.'], ['Not a cloud over the ridge.', 'The haulers will be glad.'], ['Warm for the hour.', 'Warm enough to sit out, anyway.']],
  rain: [['This rain.', 'Good for the fields, bad for my boots.'], ['Wet again.', 'The cistern will be full, at least.'], ['Did you get caught in it?', 'To the skin. Twice.']],
  snow: [['Look at it come down.', 'Beautiful — until you have to haul in it.'], ['Snow already.', 'The Frost came early this year.'], ['My hands have not been warm in a week.', 'Come by the fabricator. It runs hot.']],
};
const HOURS: Array<[number, number, string[][]]> = [
  [0.0, 0.2, [['Cannot sleep either?', 'The night shift keeps its own hours.'], ['Quiet, this late.', 'Best hour of the day.']]],
  [0.2, 0.35, [['Early start.', 'The fields do not wait for the sun.'], ['Up with the birds.', 'Someone has to open the stall.']]],
  [0.35, 0.65, [['Long day.', 'Longer still before it is done.'], ['Have you eaten?', 'Not since dawn.']]],
  [0.65, 0.85, [['Nearly done for the day.', 'Then the tavern, and no arguments.'], ['The light goes fast now.', 'Walk with me a way?']]],
  [0.85, 1.01, [['Late to be out.', 'Late to be asking.'], ['Lamps are lit already.', 'And my feet are done.']]],
];

function firstSentences(s: string | string[]): [string, string] {
  const text = Array.isArray(s) ? s[0] : s;
  const parts = text.split(/(?<=[.!?…])\s+/).filter((p) => p.trim().length > 0);
  return [parts[0] ?? text, parts.slice(1).join(' ')];
}

/** The line of turn `turn` in the pair's thread: speaker 0 opens each beat, speaker 1 answers. */
export function chatterLine(key: string, turn: number, facts: ChatterFacts): { speaker: 0 | 1; text: string } {
  const beat = Math.floor(turn / 2);
  const reply = turn % 2 === 1;
  const u = mix01(`${key}:${beat}`);
  const v = mix01(`${key}:${beat}:line`);
  // 62 % of beats are lore, the rest the day's small talk.
  if (u < 0.62 && LORE.length > 0) {
    const e = pick(LORE, mix01(`${key}:${beat}:lore`));
    const [s1, s2] = firstSentences(e.s);
    if (!reply) {
      const tpl = pick(OPENERS, v);
      const l = e.l.replace(/^the\s+/i, '');
      return { speaker: 0, text: fit(tpl.replace('{L}', cap(l)).replace('{l}', l.toLowerCase()).replace('{S}', s1)) };
    }
    return { speaker: 1, text: fit(s2 && s2.length < 110 && mix01(`${key}:${beat}:r`) < 0.6 ? s2 : pick(REACTIONS, v)) };
  }
  const hour = HOURS.find(([a, b]) => facts.tod >= a && facts.tod < b)?.[2] ?? HOURS[2][2];
  const pool = mix01(`${key}:${beat}:w`) < 0.5 ? (SMALL_TALK[facts.precip] ?? SMALL_TALK.none) : hour;
  const line = pick(pool, v);
  return reply ? { speaker: 1, text: line[1] } : { speaker: 0, text: line[0] };
}

function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }

/** A bubble holds three short lines: cut a long sentence at a word, with an ellipsis. */
export const CHATTER_MAX_CHARS = 118;
function fit(s: string): string {
  const t = s.trim();
  if (t.length <= CHATTER_MAX_CHARS) return t;
  const cut = t.slice(0, CHATTER_MAX_CHARS - 1);
  const at = cut.lastIndexOf(' ');
  return `${(at > 60 ? cut.slice(0, at) : cut).trimEnd()}…`;
}

export interface ChatterSlot { id: string; x: number; z: number; state: string; indoors: boolean }
export interface ChatterPair { key: string; a: string; b: string; dist: number }

/**
 * The conversations near the player: every chatting versona paired with its
 * nearest chatting neighbor within CHATTER_PAIR_M, deduped, nearest first,
 * capped at CHATTER_MAX_THREADS. Indoor talkers are never overheard from
 * the street.
 */
export function selectPairs(slots: ReadonlyArray<ChatterSlot>, px: number, pz: number): ChatterPair[] {
  const talkers = slots.filter((s) => CHATTER_STATES.has(s.state) && !s.indoors && Math.hypot(s.x - px, s.z - pz) <= CHATTER_RANGE_M);
  const seen = new Set<string>();
  const out: ChatterPair[] = [];
  for (const s of talkers) {
    let best: ChatterSlot | null = null, bd = CHATTER_PAIR_M * CHATTER_PAIR_M;
    for (const o of slots) {
      if (o.id === s.id || !CHATTER_STATES.has(o.state) || o.indoors) continue;
      const d = (o.x - s.x) ** 2 + (o.z - s.z) ** 2;
      if (d < bd) { bd = d; best = o; }
    }
    if (!best) continue;
    const key = pairKey(s.id, best.id);
    if (seen.has(key)) continue;
    seen.add(key);
    const mx = (s.x + best.x) / 2, mz = (s.z + best.z) / 2;
    out.push({ key, a: s.id < best.id ? s.id : best.id, b: s.id < best.id ? best.id : s.id, dist: Math.hypot(mx - px, mz - pz) });
  }
  out.sort((p, q) => p.dist - q.dist);
  return out.slice(0, CHATTER_MAX_THREADS);
}

/** Legibility by distance: 1 inside CHATTER_FULL_M, 0 at CHATTER_RANGE_M. */
export function chatterOpacity(dist: number): number {
  if (dist <= CHATTER_FULL_M) return 1;
  if (dist >= CHATTER_RANGE_M) return 0;
  return 1 - (dist - CHATTER_FULL_M) / (CHATTER_RANGE_M - CHATTER_FULL_M);
}

/** The lore pool size (harness). */
export const CHATTER_LORE_COUNT = LORE.length;
